import { GlobalRemyProvider } from "@/components/ai/GlobalRemy";
import { MetaTags } from '@/components/MetaTags';
import { ToastContainer } from "@/components/ui/toast";
import { clearAuthData, debugAuthState, getAuthTraces } from '@/lib/auth/debug';
import { authMonitor } from '@/lib/auth/monitoring';
import { useUnifiedAuth } from "@/lib/auth/use-unified-auth";
import { unifiedAuthService } from '@/lib/auth/unified-auth-service';
import {
  isSupabaseAuthStorageKey,
  supabase,
  SUPABASE_AUTH_STORAGE_KEY,
} from '@/lib/supabase/client';
import { storageBackup } from '@/lib/supabase/storage-backup';
import { logger } from '@/lib/utils/logger';
import React,{ Suspense,lazy } from "react";
import { HelmetProvider } from "react-helmet-async";
import {
Navigate,
Route,
BrowserRouter as Router,
Routes,
} from "react-router-dom";
import { Footer } from "./components/Footer";
import { Navigation } from "./components/Navigation";
import { LazyRouteErrorBoundary } from "./components/routing/LazyRouteErrorBoundary";
import { NetworkErrorBanner } from "./components/ui/NetworkErrorBanner";
import { Home } from "./pages/Home";
// Eager public SEO route — MetaTags must mount with the shell, not after a lazy chunk
import { About } from "./pages/About";
import { LoadingScreen } from "@/components/ui/LoadingScreen";

const Privacy = lazy(() => import("./pages/legal/Privacy").then(m => ({ default: (m as any).Privacy })));
const Terms = lazy(() => import("./pages/legal/Terms").then(m => ({ default: (m as any).Terms })));
const Cookies = lazy(() => import("./pages/legal/Cookies").then(m => ({ default: (m as any).Cookies })));
const AuthCallback = lazy(() => import("./pages/auth/Callback").then(m => ({ default: (m as any).AuthCallback })));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword").then(m => ({ default: (m as any).ResetPassword })));
const Login = lazy(() => import("./pages/auth/Login").then(m => ({ default: m.Login })));
const Contact = lazy(() => import("./pages/Contact").then(m => ({ default: (m as any).Contact })));
// Quiet vouched-prospect page — direct link only, never in public nav.
const Lookup = lazy(() => import("./pages/Lookup").then(m => ({ default: m.Lookup })));
// REN-215 (option a): the Portal is /profile's successor. /profile and /profile-v2
// now redirect here unconditionally; legacy Profile.tsx is archived to pages/_disabled/.
const Portal = lazy(() => import("./pages/Portal"));
const DataImport = lazy(() => import("./pages/DataImport").then(m => ({ default: (m as any).DataImport })));
const TripSharePage = lazy(() => import("./pages/trip/[token]"));
const TasteSharePage = lazy(() => import("./pages/taste/[shortcode]"));
const SharePage = lazy(() => import("./pages/l/[shareToken]"));
const InvitePage = lazy(() => import("./pages/i/[token]").then(m => ({ default: m.InvitePage })));
const NightViewPage = lazy(() => import("./pages/w/index").then(m => ({ default: m.NightViewPage })));
const Admin = lazy(() => import("./pages/admin"));
const AdminAccessRequests = lazy(() => import("./pages/AdminAccessRequests").then(module => ({ default: module.AdminAccessRequests })));
const AdminTripRoster = lazy(() => import("./pages/admin/TripRoster").then(module => ({ default: module.AdminTripRoster })));
const CollectionShowcase = lazy(() => import("./pages/CollectionShowcase"));
const PublicCollection = lazy(() => import("./pages/PublicCollection"));
const CuratedList = lazy(() => import("./pages/CuratedList").then(m => ({ default: m.CuratedList })));
const UnifiedHomeMobile = lazy(() => import("./pages/UnifiedHomeMobile").then(module => ({ default: module.UnifiedHomeMobile })));
const ExperienceTemplate = lazy(() => import("./pages/experiences/ExperienceTemplate"));

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(false);
  
  React.useEffect(() => {
    const checkMobile = () => {
      const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || 
                    window.innerWidth < 768;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
}

function ProtectedRoute({
  children,
  requiredRole,
}: {
  children: React.ReactNode;
  requiredRole?: string | string[];
}) {
  const {
    user,
    profile,
    isLoading: loading,
    error: profileError,
    refreshProfile,
  } = useUnifiedAuth();
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  React.useEffect(() => {
    if (!loading) {
      setIsInitialLoad(false);
    }
  }, [loading]);

  if (loading && isInitialLoad) {
    return <LoadingScreen />;
  }

  if (!loading && !user) {
    return <Navigate to="/" replace />;
  }

  // Check role requirements — wait for members profile; never silent-bounce on load failure
  if (user && requiredRole) {
    if (!profile && !profileError) {
      return <LoadingScreen />;
    }

    if (profileError && !profile) {
      return (
        <div className="min-h-screen bg-earth-900 flex items-center justify-center px-4">
          <div className="max-w-md text-center space-y-4">
            <h1 className="text-xl font-brand-display text-bone">Profile unavailable</h1>
            <p className="text-sand-400 text-sm">
              We could not verify your admin access from your member profile.
              This is not a silent redirect — retry or check that your account
              has <code className="text-sand-300">members.is_admin</code> set.
            </p>
            <p className="text-sand-500 text-xs break-words">{profileError.message}</p>
            <button
              type="button"
              onClick={() => { void refreshProfile(); }}
              className="px-4 py-2 bg-olive text-bone rounded"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    const allowedRoles = Array.isArray(requiredRole)
      ? requiredRole
      : [requiredRole];

    // Use profile role from unified auth (sourced from members.is_admin / members.role)
    const userRole = profile?.role || 'user';
    if (!allowedRoles.includes(userRole) && userRole !== "admin") {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}

function App() {
  // Clear any bad tokens immediately on app startup
  // This prevents the auth system from attempting to use invalid refresh tokens
  // which would cause 400 errors and retry loops. By detecting and clearing
  // malformed tokens early, we ensure a clean auth initialization.
  React.useEffect(() => {
    const checkAndClearBadTokens = async () => {
      try {
        const authKey = SUPABASE_AUTH_STORAGE_KEY;
        const storedAuth = localStorage.getItem(authKey);
        
        if (storedAuth) {
          try {
            const parsed = JSON.parse(storedAuth);
            // Supabase refresh tokens are often opaque (not JWTs). Only clear when
            // the value is empty or looks like a *broken* JWT (has dots but ≠3 parts).
            if (parsed.refresh_token) {
              const refreshToken = String(parsed.refresh_token);
              const tokenParts = refreshToken.split('.');
              const looksLikeBrokenJwt = refreshToken.includes('.') && tokenParts.length !== 3;
              if (!refreshToken || looksLikeBrokenJwt) {
                logger.warn('Malformed refresh token detected, clearing session');
                localStorage.removeItem(authKey);
                await storageBackup.clear(authKey);
                await supabase.auth.signOut();
                return;
              }

              if (parsed.expires_at && parsed.expires_at * 1000 < Date.now()) {
                logger.debug('Access token expired, will attempt refresh');
              }
            }
          } catch (e) {
            logger.error('Failed to parse auth token, clearing:', e);
            localStorage.removeItem(authKey);
            await storageBackup.clear(authKey);
          }
        }
      } catch (error) {
        logger.error('Error checking tokens:', error);
      }
    };
    
    checkAndClearBadTokens();
  }, []);

  // Always call hooks at the top, before any return
  const { user, isLoading: loading } = useUnifiedAuth();

  React.useEffect(() => {
    import('@/lib/voice/prewarm-voice').then(({ shouldPrewarmVoice, prewarmVoiceSession }) => {
      if (shouldPrewarmVoice()) {
        console.log('[App] Prewarming voice session for frequent user');
        prewarmVoiceSession();
      } else {
        // New user - prewarm after 10 seconds of activity
        const timer = setTimeout(() => {
          console.log('[App] Prewarming voice session after delay');
          prewarmVoiceSession();
        }, 10000);
        return () => clearTimeout(timer);
      }
    });
  }, []);

  // Add periodic session validation to prevent stale tokens
  React.useEffect(() => {
    // Monitor localStorage directly to debug session loss - DEV only
    if (import.meta.env.DEV) {
      let lastLocalStorageCount = 0;
      const monitorStorage = () => {
        const allKeys = Object.keys(localStorage);
        const authKeys = allKeys.filter((key) => isSupabaseAuthStorageKey(key));
        const hasAuthKey = authKeys.length > 0;
        const hasSessionBackup = Object.keys(sessionStorage).some((key) =>
          isSupabaseAuthStorageKey(key)
        );
        
        if (
          (lastLocalStorageCount > 0 && allKeys.length === 0) ||
          (!hasAuthKey && hasSessionBackup)
        ) {
          logger.error('localStorage auth state was cleared. Attempting recovery...');
          
          void storageBackup.restore(SUPABASE_AUTH_STORAGE_KEY).then((recoveredValue) => {
            if (recoveredValue) {
              try {
                localStorage.setItem(SUPABASE_AUTH_STORAGE_KEY, recoveredValue);
                logger.info('Recovered auth token from backup system');
              } catch (e) {
                logger.error('Failed to restore auth token:', e);
              }
            } else {
              logger.debug('No auth token found in backup system');
            }
          });
          
          setTimeout(() => {
            logger.debug('Triggering auth state check after recovery...');
            supabase.auth.getSession();
          }, 500);
        }
        
        lastLocalStorageCount = allKeys.length;
        
        logger.debug(`localStorage: ${allKeys.length} total keys, ${authKeys.length} auth keys`);
        authKeys.forEach(key => {
          const value = localStorage.getItem(key);
          if (value && isSupabaseAuthStorageKey(key)) {
            try {
              const parsed = JSON.parse(value);
              logger.debug(`${key} has keys:`, Object.keys(parsed).join(', '));
            } catch {
              logger.warn(`${key} has invalid JSON`);
            }
          }
        });
      };

      const handleStorageChange = (e: StorageEvent) => {
        if (
          e.storageArea === localStorage &&
          isSupabaseAuthStorageKey(e.key) &&
          !e.newValue
        ) {
          const authKey = e.key;
          logger.warn('Auth key removed from localStorage:', e.key);
          void storageBackup.restore(authKey).then((recoveredValue) => {
            if (recoveredValue) {
              logger.info('Immediately restoring from backup system');
              try {
                localStorage.setItem(authKey, recoveredValue);
              } catch (err) {
                logger.error('Failed to restore key:', err);
              }
            }
          });
        }
      };
      
      window.addEventListener('storage', handleStorageChange);
      
      // Initial check after a short delay to ensure auth is set up
      const monitorTimeoutId = setTimeout(monitorStorage, 1000);
      
      const monitorInterval = setInterval(monitorStorage, 2000);

      return () => {
        clearTimeout(monitorTimeoutId);
        clearInterval(monitorInterval);
        window.removeEventListener('storage', handleStorageChange);
      };
    }

    // IMPORTANT: This is needed to migrate users from old auth system
    // Users with old tokens get stuck on loading page without this
    const checkSession = async () => {
      const currentUser = user; // Use the hook value directly
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          logger.warn('Session check error:', error.message);
        }
        if (session || !currentUser) return;

        // No session but UI thinks user is logged-in → try silent refresh
        const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshed.session) {
          logger.info('Session silently refreshed');
          return;
        }
        logger.warn('Session refresh failed:', refreshError?.message);
        // Don't force sign-out on refresh failure - let user stay logged in
        // The auth system will handle actual auth failures when making API calls
        logger.info('Session refresh failed but keeping user logged in');
        // Only log the issue, don't sign out
      } catch (err) {
        logger.error('Session validation unexpected error:', err);
      }
    };

    // Initial check after mount - increased delay to avoid race conditions
    const timeoutId = setTimeout(checkSession, 30000); // 30 seconds
    
    // Periodic checks every 5 minutes (much less aggressive)
    const intervalId = setInterval(checkSession, 300000); // 5 minutes

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [user]); // Add user dependency


  const isMobile = useIsMobile();

  // Publish development-only auth helpers once per App mount. Keeping this
  // work in the render body repeated it on every render and made test coverage
  // timing-dependent. These modules are already in the main graph, so reusing
  // their static instances also avoids ineffective mixed dynamic imports.
  React.useEffect(() => {
    if (typeof window === 'undefined' || !import.meta.env.DEV) return;

    window.supabase = supabase;
    window.unifiedAuthService = unifiedAuthService;
    window.resetAuth = async () => {
      logger.info('Manual auth reset triggered');
      await unifiedAuthService.signOut();
    };
    window.debugAuthState = debugAuthState;
    window.clearAuthData = clearAuthData;
    window.getAuthTraces = getAuthTraces;
    window.authMonitor = authMonitor;
    logger.debug('Debug utilities loaded');
    logger.debug('Monitoring loaded');

    void import('./lib/auth/fix-loading')
      .then(({ fixAuthLoading, resetAuthCompletely }) => {
        window.fixAuthLoading = fixAuthLoading;
        window.resetAuthCompletely = resetAuthCompletely;
        logger.debug('Fix utilities loaded');
      })
      .catch((err) => logger.error('Failed to load fix utilities:', err));

    void import('./lib/auth/status')
      .then(({ authSystemStatus }) => {
        window.authSystemStatus = authSystemStatus;
        logger.debug('Status dashboard loaded');
      })
      .catch((err) => logger.error('Failed to load status:', err));
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }


  return (
    <HelmetProvider>
      <MetaTags 
        title="rendezvous - Curated Experiences"
        description="Discover extraordinary moments through curated experiences that inspire, transform, and connect."
        image="https://rendezvouswebsite.b-cdn.net/DSCF9542.JPG"
        url="https://letsrendezvous.co"
      />

      <Router>
        <GlobalRemyProvider>
          <div className="min-h-screen bg-background text-foreground font-body selection:bg-brand-rust selection:text-white">
            <NetworkErrorBanner />
            <Navigation />
            <ToastContainer />
            <main>
            <LazyRouteErrorBoundary>
            <Suspense fallback={<LoadingScreen />}>
              <Routes>
                {/* Mobile-First Experience — uniform config-driven template per slug */}
                <Route path="/basel-miami-2025" element={<ExperienceTemplate slug="basel-miami-2025" />} />
                <Route path="/miami-basel-2025" element={<Navigate to="/basel-miami-2025" replace />} />
                <Route path="/rio-decomp" element={<ExperienceTemplate slug="rio-decomp" />} />
                <Route path="/zona-maco" element={<ExperienceTemplate slug="zona-maco" />} />

                {/* Public Routes - UnifiedHomeMobile for mobile, Home for desktop */}
                <Route path="/" element={isMobile ? <UnifiedHomeMobile /> : <Home />} />
                {/* Members portal (W3) — stay-on-route auth gate lives inside Portal */}
                <Route path="/portal" element={<Portal />} />
                <Route path="/about" element={<About />} />
                {/* REN-215: /profile-v2 is superseded by the Portal. */}
                <Route path="/profile-v2" element={<Navigate to="/portal" replace />} />
                <Route path="/import-data" element={<DataImport />} />
                <Route path="/login" element={<Login />} />
                {/* "Get the app" (OPEN_IN_APP_URL = https://letsrendezvous.co/app) lands on web
                    sign-in until the App Store launch (decision 2026-10-11). Swap this one line
                    for the store / universal link then. */}
                <Route path="/app" element={<Navigate to="/login" replace />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/auth/reset-password" element={<ResetPassword />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/privacy-policy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/terms-of-service" element={<Terms />} />
                <Route path="/legal/privacy" element={<Navigate to="/privacy" replace />} />
                <Route path="/legal/terms" element={<Navigate to="/terms" replace />} />
                <Route path="/legal/cookies" element={<Cookies />} />
                <Route path="/contact" element={<Contact />} />
                {/* Quiet vouched-prospect page — direct link only, no nav entry */}
                <Route path="/lookup" element={<Lookup />} />

                {/* Public Share Routes */}
                <Route path="/trip/:token" element={<TripSharePage />} />
                <Route path="/c" element={<PublicCollection />} />
                <Route path="/c/:token" element={<PublicCollection />} />
                <Route path="/taste/:shortcode" element={<TasteSharePage />} />
                <Route path="/l" element={<SharePage />} />
                <Route path="/l/:shareToken" element={<SharePage />} />
                <Route path="/i" element={<InvitePage />} />
                <Route path="/i/:token" element={<InvitePage />} />
                <Route path="/w" element={<NightViewPage />} />
                <Route path="/w/:token" element={<NightViewPage />} />
                <Route path="/lists/:slug" element={<CuratedList />} />

                {/* Test Pages */}
                <Route path="/collections-showcase" element={<CollectionShowcase />} />

                {/* REN-215: /profile is superseded by the Portal. */}
                <Route path="/profile" element={<Navigate to="/portal" replace />} />

                {/* Admin Routes */}
                <Route
                  path="/admin/access-requests"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminAccessRequests />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/trip-roster"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminTripRoster />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/*"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <Admin />
                    </ProtectedRoute>
                  }
                />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
            </LazyRouteErrorBoundary>
          </main>
          <Footer />
        </div>
        </GlobalRemyProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;

declare global {
  interface Window {
    supabase: typeof supabase;
    unifiedAuthService: any;
    resetAuth: () => void;
    debugAuthState: any;
    clearAuthData: any;
    getAuthTraces: any;
    authMonitor: any;
    fixAuthLoading: any;
    resetAuthCompletely: any;
    authSystemStatus: any;
  }
}
